import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { CurecallInputLabel, CurecallToggle } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useEffect, useId, useState } from 'react'
import { TChatbotGeneralFormValues } from '../chatbot-form.type'
import { formatCombinedIsoDuration, parseCombinedIsoDuration } from 'src/utils/duration-iso.util'
import { DurationInputFields } from 'src/components/duration-input-fields/duration-input-fields.component'

type TDelayState = { days: number; hours: number; minutes: number }

export function ChatbotAutoRelaunchField() {
  const { t } = useTranslation()
  const id = useId()

  const {
    formState: { errors },
    control,
    setValue,
    clearErrors,
  } = useFormContext<TChatbotGeneralFormValues>()

  const automaticRelaunchDelay = useWatch({
    control,
    name: 'options.automaticRelaunchDelay',
  })

  const [delay, setDelay] = useState<TDelayState>({ days: 0, hours: 0, minutes: 0 })

  useEffect(() => {
    if (automaticRelaunchDelay) {
      const parsed = parseCombinedIsoDuration(automaticRelaunchDelay)
      setDelay({ days: parsed.days, hours: parsed.hours, minutes: parsed.minutes })
    }
  }, [automaticRelaunchDelay])

  const updateDelay = (newDelay: TDelayState) => {
    setDelay(newDelay)
    setValue('options.automaticRelaunchDelay', formatCombinedIsoDuration(newDelay))
    clearErrors('options.automaticRelaunchDelay')
  }

  return (
    <div className="c-flex c-flex-col c-gap-4">
      <Controller
        control={control}
        name="options.automaticRelaunchDelay"
        render={({ field }) => (
          <CurecallToggle
            leftLabel={t('pages.configuration.followups.chatbot.general-form.automatic-relaunch')}
            isEnabled={!!field.value}
            onChange={(enabled) => {
              if (enabled) {
                setDelay({ days: 0, hours: 0, minutes: 0 })
                setValue(
                  'options.automaticRelaunchDelay',
                  formatCombinedIsoDuration({ days: 0, hours: 0, minutes: 0 }),
                )
              } else {
                setValue('options.automaticRelaunchDelay', null)
                clearErrors('options.automaticRelaunchDelay')
                setDelay({ days: 0, hours: 0, minutes: 0 })
              }
            }}
            tooltipContent={t(
              'pages.configuration.followups.chatbot.general-form.automatic-relaunch-tooltip',
            )}
            tooltipPosition="right"
          />
        )}
      />

      {automaticRelaunchDelay && (
        <div className="c-flex c-flex-col c-pl-6 c-border-l-2 c-border-gray-200 c-mb-4">
          <CurecallInputLabel
            id={id}
            label={t('pages.configuration.followups.chatbot.general-form.relaunch-delay')}
            isRequired={true}
            tooltipContent={t('pages.configuration.followups.chatbot.general-form.relaunch-delay-tooltip')}
            tooltipPosition="right"
          />
          <Controller
            control={control}
            name="options.automaticRelaunchDelay"
            rules={{
              validate: (value) => {
                if (!value) return true
                const parsed = parseCombinedIsoDuration(value)
                return parsed.days > 0 || parsed.hours > 0 || parsed.minutes > 0
              },
            }}
            render={() => (
              <div className="c-flex c-flex-col c-gap-2">
                <DurationInputFields delay={delay} onDelayChange={updateDelay} />
                {errors.options?.automaticRelaunchDelay && (
                  <span className="c-text-sm c-text-danger-600">
                    {t('pages.configuration.followups.chatbot.general-form.relaunch-delay-error')}
                  </span>
                )}
              </div>
            )}
          />
        </div>
      )}
    </div>
  )
}
